import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { usePendingLeaveApi } from './config/usePendingLeaveApi';
import { parseTableParams } from '../../utils/Utils';
import { setSignatureStatus } from '../../services/leaveService';
import DataTable from '../../components/ui/DataTable';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import TextInput from '../../components/ui/TextInput.jsx';
import Button from '../../components/ui/Button';

const Container = styled.div``;

const RejectOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const RejectDialog = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 28px;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
`;

const RejectTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 16px;
`;

export default function PendingApprovalsPage() {
  const { t } = useTranslation();
  const history = useHistory();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [approveConfirm, setApproveConfirm] = useState({ isOpen: false, id: null });
  const [rejectDialog, setRejectDialog] = useState({ isOpen: false, id: null });
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const { columns: baseColumns, data, fetching, pagination, approve, reject, refresh } = usePendingLeaveApi(queryParams);

  const handleSetSignature = useCallback(async (row, next) => {
    try {
      await setSignatureStatus(row.id, next);
      refresh?.();
    } catch { /* interceptor */ }
  }, [refresh]);

  const handleApproveClick = useCallback((row) => {
    setApproveConfirm({ isOpen: true, id: row.id });
  }, []);

  const handleApproveConfirm = useCallback(async () => {
    setProcessing(true);
    await approve(approveConfirm.id);
    setApproveConfirm({ isOpen: false, id: null });
    setProcessing(false);
  }, [approve, approveConfirm.id]);

  const handleRejectOpen = useCallback((row) => {
    setRejectDialog({ isOpen: true, id: row.id });
    setRejectReason('');
  }, []);

  const handleRejectConfirm = useCallback(async () => {
    if (!rejectReason.trim()) return;
    setProcessing(true);
    await reject(rejectDialog.id, rejectReason);
    setRejectDialog({ isOpen: false, id: null });
    setProcessing(false);
  }, [reject, rejectDialog.id, rejectReason]);

  const handleDetails = useCallback((row) => {
    history.push(`/leaves/${row.id}`);
  }, [history]);

  const handleRowClick = useCallback((row) => {
    
    history.push(`/leaves/${row.id}`);
  }, [history]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const actionOptions = useMemo(() => [
    { type: 'approve', label: t('leave.approve'), onClick: handleApproveClick },
    { type: 'reject', label: t('leave.reject'), onClick: handleRejectOpen },
    {
      type: 'markAsSigned',
      label: t('leave.markAsSigned'),
      onClick: (row) => handleSetSignature(row, 'signed'),
      disabled: (row) => row?.signatureStatus === 'signed',
    },
    {
      type: 'markAsUnsigned',
      label: t('leave.markAsUnsigned'),
      onClick: (row) => handleSetSignature(row, 'unsigned'),
      disabled: (row) => row?.signatureStatus !== 'signed',
    },
    { type: 'divider' },
    { type: 'details', label: t('leave.detail'), onClick: handleDetails },
  ], [t, handleApproveClick, handleRejectOpen, handleDetails, handleSetSignature]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <OptionsMenu actions={actionOptions} row={row} />;
        },
      },
    },
  ], [baseColumns, actionOptions, data]);

  return (
    <Container>
      <ConfirmDialog
        isOpen={approveConfirm.isOpen}
        title={t('leave.approveTitle')}
        message={t('leave.approveMessage')}
        onConfirm={handleApproveConfirm}
        onCancel={() => setApproveConfirm({ isOpen: false, id: null })}
        loading={processing}
      />

      {rejectDialog.isOpen && (
        <RejectOverlay onClick={() => setRejectDialog({ isOpen: false, id: null })}>
          <RejectDialog onClick={(e) => e.stopPropagation()}>
            <RejectTitle>{t('leave.rejectTitle')}</RejectTitle>
            <TextInput
              label={t('leave.rejectReason')}
              type="textarea"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              required
              rows={3}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
              <Button variant="outline" onClick={() => setRejectDialog({ isOpen: false, id: null })} disabled={processing}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleRejectConfirm} disabled={processing || !rejectReason.trim()}
                style={{ background: '#d32f2f' }}>
                {processing ? '...' : t('leave.reject')}
              </Button>
            </div>
          </RejectDialog>
        </RejectOverlay>
      )}

      {useMemo(() => (
        <DataTable
          title={t('leave.pendingApprovals')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={handleRowClick}
        />
      ), [
        t, fetching, columns, data, pagination,
        filterList, handleFilterConfirm, handleDataStateChange, handleRowClick,
      ])}
    </Container>
  );
}
